import React from 'react';
import { useSelector } from 'react-redux';
import { getHomeViewRole } from '@app/selectors/home';
import {
  Box,
  Typography,
  useTheme,
  Link,
  Icons,
  Tooltip,
} from '@ublend-npm/aulaui-core';
import { getStarredSpaceColors } from '../../colors';
import { trackHomeWidgetEmptyCtaClicked } from '../common';
import { NAVIGATION_SOURCE_STARRED_SPACES } from '@core/constants/analytics';

interface StarredSpacesEmptyStateProps {
  helpArticleUrl?: string;
  onTrigger?: () => void;
}

const STRINGS = {
  startSpaceEasy: 'Star spaces to keep them easy to find.',
  learnHow: ' Learn how to star spaces here (link to HC article)',
  noSpaces: 'No spaces currently',
};

const StarredSpacesEmptyState: React.FC<StarredSpacesEmptyStateProps> = ({
  helpArticleUrl = '#',
  onTrigger,
}) => {
  const { palette } = useTheme();
  const colors = getStarredSpaceColors(palette);
  const role = useSelector(getHomeViewRole);

  const handleAddSpaceClick = () => {
    trackHomeWidgetEmptyCtaClicked(
      NAVIGATION_SOURCE_STARRED_SPACES,
      'Starred Spaces',
      'add_space_icon',
      undefined,
      role
    );
    if (onTrigger) {
      onTrigger();
    }
  };

  const handleHelpCenterClick = () => {
    trackHomeWidgetEmptyCtaClicked(
      NAVIGATION_SOURCE_STARRED_SPACES,
      'Starred Spaces',
      'help_center',
      undefined,
      role
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      bgcolor={colors.emptyPanelBg}
      borderRadius="0.5rem"
      p={{ xs: 2, md: 3 }}
      gap={0.5}
      flex={1}
      width="100%"
      minHeight={{ xs: '150px', md: '200px' }}
      sx={{
        transition: 'background-color 0.2s',
        boxSizing: 'border-box',
      }}
    >
      {(() => {
        const addButton = (
          <Box
            onClick={onTrigger ? handleAddSpaceClick : undefined}
            sx={{
              cursor: onTrigger ? 'pointer' : 'inherit',
              '&:hover': onTrigger ? { opacity: 0.8 } : {},
            }}
            role={onTrigger ? 'button' : undefined}
            tabIndex={onTrigger ? 0 : undefined}
            onKeyDown={(e) => {
              if (onTrigger && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onTrigger();
              }
            }}
          >
            <Icons.AddButtonIcon
              mode={palette.mode === 'dark' ? 'dark' : 'light'}
            />
          </Box>
        );

        if (onTrigger) {
          return addButton;
        }

        return (
          <Tooltip title={STRINGS.noSpaces} placement="top" arrow>
            {addButton}
          </Tooltip>
        );
      })()}

      <Typography
        color={colors.emptyTitleColor}
        sx={{
          fontFamily: (theme) => theme.typography.fontFamily,
          fontWeight: 'regular',
          fontStyle: 'normal',
          fontSize: '1rem',
          lineHeight: '175%',
          letterSpacing: '0.15px',
          textAlign: 'center',
        }}
      >
        {STRINGS.startSpaceEasy}
      </Typography>

      <Link
        href={helpArticleUrl}
        newTab
        rel="noopener noreferrer"
        onClick={handleHelpCenterClick}
        sx={{
          color: colors.linkColor,
          textDecoration: 'underline',
          textDecorationStyle: 'solid',
          textUnderlineOffset: '3px',
          fontWeight: 'regular',
          fontStyle: 'normal',
          fontFamily: (theme) => theme.typography.fontFamily,
          cursor: 'pointer',
          fontSize: '0.75rem',
          lineHeight: 1.5,
          letterSpacing: '0px',
          textAlign: 'center',
        }}
      >
        {STRINGS.learnHow}
      </Link>
    </Box>
  );
};

export default StarredSpacesEmptyState;
